import { api, unwrap } from '../../shared/api/client';
import type { components, operations } from '../../shared/api/schema.gen';

export type TRunRequest = components['schemas']['RunRequestResDto'];
export type TRequestAuth = components['schemas']['RequestAuthDto'];
export type TRunRequestRaw = components['schemas']['RunRequestRawResDto'];
export type TRunNeighbours = components['schemas']['RunNeighboursResDto'];
export type TRunNeighboursQuery = NonNullable<
  operations['RunNeighboursController_neighbours']['parameters']['query']
>;

// `at` is the copy's own timestamp: one partition, not every one in retention.
export const copyRequest = (copyId: string, at?: string): Promise<TRunRequest> =>
  unwrap(
    api.GET('/v1/events/{id}/request', {
      params: { path: { id: copyId }, query: at ? { at } : {} },
    }),
  );

// Journalled on the server: asked only when somebody opens Raw, never on page load.
export const copyRequestRaw = (copyId: string, at?: string): Promise<TRunRequestRaw> =>
  unwrap(
    api.GET('/v1/events/{id}/request/raw', {
      params: { path: { id: copyId }, query: at ? { at } : {} },
    }),
  );

export const runNeighbours = (runId: string, query: TRunNeighboursQuery): Promise<TRunNeighbours> =>
  unwrap(api.GET('/v1/runs/{id}/neighbours', { params: { path: { id: runId }, query } }));

export const requestKeys = {
  request: (copyId: string) => ['runs', 'request', copyId] as const,
  raw: (copyId: string) => ['runs', 'request-raw', copyId] as const,
  neighbours: (runId: string, query: TRunNeighboursQuery) =>
    ['runs', 'neighbours', runId, query] as const,
};

const AUTH_METHOD: Record<string, string> = {
  hmac: 'signature (HMAC)',
  basic: 'basic auth',
  api_key: 'API key',
  bearer: 'bearer token',
  ip_allowlist: 'IP allowlist',
  none: 'none',
};

export const authMethodLabel = (method: string): string => AUTH_METHOD[method] ?? method;

const SECRET_SLOT: Record<string, string> = {
  active: 'current secret',
  grace: 'previous secret, in its grace period',
  expired: 'expired secret',
  revoked: 'revoked secret',
};

export const secretSlotLabel = (slot: string): string => SECRET_SLOT[slot] ?? slot;

const FAILURE_REASON: Record<string, string> = {
  missing_header: 'the signature header was missing',
  malformed_header: 'the signature header could not be read',
  no_credential_matched: 'no secret produced this signature',
  no_credential_configured: 'the source has no secret to check against',
  timestamp_out_of_tolerance: 'the signed timestamp was outside the tolerance',
  timestamp_missing: 'the signed timestamp was missing',
  ip_not_allowed: 'the sender’s address is not on the allowlist',
  ip_undetermined: 'the sender’s address could not be determined',
  unsupported_scheme: 'the signature scheme is not one this source accepts',
};

export const failureReasonLabel = (reason: string): string => FAILURE_REASON[reason] ?? reason;

export const skewText = (ms: number): string => `${(ms / 1000).toFixed(1)} s skew`;
